// Standalone chunk error recovery module.
// Imported in main.ts as a side-effect import before app.mount().
// Uses only native browser APIs — no Vue, no pinia, no useRegisterSW.
// Source: Vite docs (https://vite.dev/guide/build#load-error-handling) + native SW API

import { activateWaitingServiceWorker } from '@/utilities/serviceWorkerUtils';

const RELOAD_COUNT_KEY = 'chunk-error-reload-count';
const MAX_RELOADS = 1;

let reloadInProgress = false;

// --- Session guard functions ---

function getReloadCount(): number {
    return parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) ?? '0', 10);
}

function incrementReloadCount(): void {
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(getReloadCount() + 1));
}

export function clearReloadCount(): void {
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
}

// --- Exhausted guard message ---

function showExhaustedMessage(): void {
    // Use localStorage language key — same key used by useLanguage.ts composable.
    // i18n is not initialized at this point, so we hard-code the messages.
    const lang = localStorage.getItem('language') ?? 'en';
    const messages: Record<string, string> = {
        en: 'Something went wrong. Please try refreshing the page manually.',
        sv: 'Något gick fel. Försök att uppdatera sidan manuellt.',
        nb: 'Noe gikk galt. Prøv å oppdatere siden manuelt.',
        de: 'Etwas ist schiefgelaufen. Bitte versuche, die Seite manuell zu aktualisieren.',
        nl: 'Er is iets misgegaan. Probeer de pagina handmatig te vernieuwen.',
        fr: 'Une erreur est survenue. Veuillez essayer de rafraîchir la page manuellement.',
        es: 'Algo salió mal. Por favor, intenta recargar la página manualmente.',
        it: 'Qualcosa è andato storto. Prova ad aggiornare la pagina manualmente.',
        pl: 'Coś poszło nie tak. Spróbuj odświeżyć stronę ręcznie.',
        fi: 'Jokin meni pieleen. Yritä päivittää sivu manuaalisesti.',
    };
    const msg = messages[lang] ?? messages['en'];

    const loadingScreen = document.getElementById('app-loading-screen');
    if (loadingScreen) {
        // Loading screen is still in DOM (fired before or shortly after mount).
        // #reload contains a <p class="loading-screen-info"> and a reload button.
        // Adding class 'active' fades in the div via CSS opacity transition.
        const reloadEl = document.getElementById('reload');
        if (reloadEl) {
            const p = reloadEl.querySelector('p.loading-screen-info');
            if (p) {
                p.textContent = msg;
            }
            reloadEl.classList.add('active');
        }
    } else {
        // Loading screen already removed (post-mount chunk error, edge case).
        // Insert a simple centered overlay so the user always sees a message.
        const overlay = document.createElement('div');
        overlay.setAttribute(
            'style',
            'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;' +
            'background:rgba(255,255,255,0.95);z-index:9999;font-family:sans-serif;font-size:1rem;' +
            'padding:2rem;text-align:center;'
        );
        overlay.textContent = msg;
        document.body.appendChild(overlay);
    }
}

// --- Core recovery function ---

async function performRecovery(source: 'preloadError' | 'unhandledrejection', error: unknown): Promise<void> {
    // Cascade prevention: if a reload is already in progress, ignore subsequent events.
    if (reloadInProgress) return;

    console.warn(`[ChunkError] Detected via ${source}:`, error);

    if (getReloadCount() >= MAX_RELOADS) {
        console.warn('[ChunkError] Guard exhausted — showing manual reload message');
        showExhaustedMessage();
        return;
    }

    reloadInProgress = true;
    incrementReloadCount();

    // If the Vue app has mounted, window.reloadApp routes through vite-plugin-pwa's
    // updateServiceWorker() which triggers a single clean reload via its internal
    // 'controlling' event. Using the native API directly would cause a double reload
    // because vite-plugin-pwa's listener fires too.
    if (typeof window.reloadApp === 'function') {
        window.reloadApp();
        return;
    }

    // Fallback for chunk errors that fire before the Vue app mounts.
    try {
        await activateWaitingServiceWorker();
    } catch (swError) {
        console.warn('[ChunkError] SW check failed, falling back to plain reload:', swError);
    }

    window.location.reload();
}

// --- Event listener 1: vite:preloadError (primary detection path) ---
// VitePreloadErrorEvent is declared in vite/client.d.ts and extends WindowEventMap,
// so addEventListener is fully typed without custom declarations.

window.addEventListener('vite:preloadError', (event: VitePreloadErrorEvent) => {
    // preventDefault() suppresses the re-throw from Vite's internal handler.
    // Without it the error propagates after our handler and may trigger additional handlers.
    event.preventDefault();
    void performRecovery('preloadError', event.payload);
});

// --- Event listener 2: unhandledrejection (fallback detection path) ---
// Catches dynamic imports that fail without going through the vite:preloadError path.
// Only matches known chunk-related error patterns to avoid false-positive reloads.

const CHUNK_ERROR_PATTERNS: RegExp[] = [
    /Failed to fetch dynamically imported module/i, // Chrome / Firefox chunk 404
    /Loading chunk \S+ failed/i,                    // Webpack-era pattern still seen in some setups
    /Importing a module script failed/i,            // Safari variant
];

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const message = (event.reason as Error)?.message ?? '';
    if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
        event.preventDefault();
        void performRecovery('unhandledrejection', event.reason);
    }
});
